import { wsLoadSummary, roleLoadSummary, wsSummary, pricing, loadEntry, roleWsEstimate, loadStatus, fmtH, fmtC, fmtPct } from "@/lib/calc";

const Th = ({ children, right }) => (
  <th className={`bg-zinc-50 border-b border-zinc-300 px-3 py-2 text-[10px] uppercase tracking-wide text-zinc-500 font-medium ${right ? "text-right" : "text-left"}`}>{children}</th>
);
const Td = ({ children, right, mono = true, className = "" }) => (
  <td className={`border-b border-zinc-100 px-3 py-2 text-[13px] ${mono ? "font-mono" : ""} ${right ? "text-right" : "text-left"} ${className}`}>{children}</td>
);

const Delta = ({ v, fmt }) => (
  <span className={v > 1 ? "text-red-600" : v < -1 ? "text-amber-600" : "text-zinc-400"}>
    {v > 0 ? "+" : ""}{fmt(v)}
  </span>
);

export default function SummaryTab({ est }) {
  const wsLoad = wsLoadSummary(est);          // cost basis (resource load)
  const wsWbs = Object.fromEntries(wsSummary(est).map((w) => [w.name, w]));  // suggested baseline
  const roles = roleLoadSummary(est);
  const p = pricing(est);

  return (
    <div data-testid="summary-tab" className="grid grid-cols-1 xl:grid-cols-3 gap-8">
      <div className="xl:col-span-2 space-y-8">
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-200">
            <h3 className="font-heading text-sm font-medium tracking-tight">Effort &amp; cost by workstream</h3>
            <p className="text-[11px] text-zinc-400 mt-0.5">Cost basis = resource load · WBS suggested baseline shown for reference</p>
          </div>
          <table className="w-full">
            <thead><tr><Th>Workstream</Th><Th right>Load hrs</Th><Th right>Load cost</Th><Th right>WBS suggested</Th><Th right>Δ cost</Th></tr></thead>
            <tbody>
              {wsLoad.map((w) => {
                const sug = wsWbs[w.name]?.totalCost || 0;
                return (
                  <tr key={w.name} className="hover:bg-zinc-50">
                    <Td mono={false}>{w.name}</Td>
                    <Td right>{fmtH(w.hours)}</Td>
                    <Td right className="font-medium">{fmtC(w.cost)}</Td>
                    <Td right className="text-zinc-400">{fmtC(sug)}</Td>
                    <Td right><Delta v={w.cost - sug} fmt={fmtC} /></Td>
                  </tr>
                );
              })}
              <tr className="bg-zinc-50 font-medium">
                <Td mono={false}>TOTAL</Td>
                <Td right>{fmtH(p.loadHours)}</Td>
                <Td right data-testid="summary-total-cost">{fmtC(p.subtotal)}</Td>
                <Td right className="text-zinc-400">{fmtC(p.suggestedCost)}</Td>
                <Td right><Delta v={p.costVariance} fmt={fmtC} /></Td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-200"><h3 className="font-heading text-sm font-medium tracking-tight">Load hours &amp; cost by role</h3></div>
          <table className="w-full">
            <thead><tr><Th>Role</Th><Th right>Load hrs</Th><Th right>Cost</Th></tr></thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.name} className="hover:bg-zinc-50">
                  <Td mono={false}>{r.name}</Td><Td right>{fmtH(r.hours)}</Td><Td right>{fmtC(r.cost)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-8">
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-200"><h3 className="font-heading text-sm font-medium tracking-tight">Pricing waterfall</h3></div>
          <table className="w-full">
            <tbody>
              {[
                ["Resource-load cost", fmtC(p.baseCost)],
                [`Margin (${fmtPct(est.margin_pct)})`, "+" + fmtC(p.marginAmount)],
                [`Partner fee (${fmtPct(est.partner_fee_pct)})`, "+" + fmtC(p.partnerFee)],
              ].map(([l, v]) => (
                <tr key={l}><Td mono={false}>{l}</Td><Td right>{v}</Td></tr>
              ))}
              <tr className="bg-[#002FA7] text-white">
                <Td mono={false} className="border-0 font-medium">Final price</Td>
                <Td right className="border-0 font-medium" data-testid="summary-final-price">{fmtC(p.finalPrice)}</Td>
              </tr>
            </tbody>
          </table>
          <div className="px-4 py-3 border-t border-zinc-100 grid grid-cols-2 gap-2">
            <div><p className="text-[10px] uppercase tracking-wide text-zinc-400">Avg cost rate</p><p className="text-sm font-mono">${p.avgCostRate.toFixed(1)}/hr</p></div>
            <div><p className="text-[10px] uppercase tracking-wide text-zinc-400">Effective bill rate</p><p className="text-sm font-mono">${p.effectiveRate.toFixed(1)}/hr</p></div>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-200"><h3 className="font-heading text-sm font-medium tracking-tight">Load vs WBS suggested baseline</h3></div>
          <table className="w-full">
            <tbody>
              <tr><Td mono={false}>Resource load (cost basis)</Td><Td right>{fmtH(p.loadHours)} hrs</Td></tr>
              <tr><Td mono={false}>WBS suggested baseline</Td><Td right>{fmtH(p.suggestedHours)} hrs</Td></tr>
              <tr><Td mono={false}>Variance</Td><Td right>{p.hoursVariance > 0 ? "+" : ""}{fmtH(p.hoursVariance)} hrs</Td></tr>
              <tr>
                <Td mono={false}>Status</Td>
                <Td right className={`font-medium ${loadStatus(p.loadHours, p.suggestedHours, Number(est.tolerance_pct) || 0.05).cls}`} data-testid="summary-recon-status">
                  {loadStatus(p.loadHours, p.suggestedHours, Number(est.tolerance_pct) || 0.05).label}
                </Td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-200"><h3 className="font-heading text-sm font-medium tracking-tight">Load variance by role</h3></div>
          <table className="w-full">
            <thead><tr><Th>Role</Th><Th right>Load</Th><Th right>WBS est.</Th><Th right>Status</Th></tr></thead>
            <tbody>
              {est.roles.map((r) => {
                let load = 0, estv = 0;
                for (const w of est.workstreams) {
                  const e = loadEntry(est, w.name, r.name);
                  load += e ? e.hours.reduce((a, b) => a + (Number(b) || 0), 0) : 0;
                  estv += roleWsEstimate(est, w.name, r.name);
                }
                const st = loadStatus(load, estv, Number(est.tolerance_pct) || 0.05);
                return (
                  <tr key={r.name} className="hover:bg-zinc-50">
                    <Td mono={false} className="text-xs">{r.name}</Td>
                    <Td right>{fmtH(load)}</Td><Td right>{fmtH(estv)}</Td>
                    <Td right className={`text-xs font-medium ${st.cls}`}>{st.label}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
